import { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, ArrowUpRight, Facebook, Linkedin, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Eyebrow } from "./Reveal";

const BG_IMG =
  "https://images.unsplash.com/photo-1467803738586-46b7eb7b16a1?q=80&auto=format&fit=crop&w=2400";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_OPTIONS = ["Any time", "Morning (9am–12pm)", "Afternoon (12pm–5pm)", "Evening (after 5pm)"];

const inputCls =
  "w-full rounded-lg border border-ink/15 bg-white px-4 py-3 text-sm text-ink placeholder:text-ink/35 outline-none transition-colors focus:border-eucalyptus";

const CallbackForm = () => {
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", email: "", preferred_time: "Any time", message: "", company: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/callbacks`, form);
      setDone(true);
      toast.success(data?.email_sent
        ? "Thanks — Tory will call you back shortly."
        : "Thanks — your request is saved and Tory will be in touch.");
      setForm({ first_name: "", last_name: "", phone: "", email: "", preferred_time: "Any time", message: "", company: "" });
    } catch {
      toast.error("Something went wrong — please call 0414 345 736 instead.");
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div data-testid="callback-success-message" className="flex h-full flex-col items-start justify-center rounded-2xl border border-ink/10 bg-ivory p-8 lg:p-10">
        <CheckCircle2 className="h-10 w-10 text-eucalyptus" />
        <h3 className="mt-6 font-display text-3xl font-medium text-ink">Request received.</h3>
        <p className="mt-3 text-sm leading-relaxed text-ink/65">
          Tory will call you back as soon as possible. Prefer to talk now?
        </p>
        <a href="tel:0414345736" data-testid="callback-success-call-link" className="mt-5 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-eucalyptus link-sweep">
          <Phone className="h-4 w-4" /> 0414 345 736
        </a>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="callback-form"
      className="rounded-2xl border border-ink/10 bg-ivory p-8 lg:p-10"
    >
      <h3 className="font-display text-3xl font-medium text-ink">Request a callback</h3>
      <p className="mt-2 text-sm text-ink/60">Leave your details and Tory will ring you — the loan review is free, with no obligation.</p>

      <div className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <input required placeholder="First name *" autoComplete="given-name" data-testid="callback-first-name-input" value={form.first_name} onChange={set("first_name")} className={inputCls} />
        <input required placeholder="Last name *" autoComplete="family-name" data-testid="callback-last-name-input" value={form.last_name} onChange={set("last_name")} className={inputCls} />
        <input required type="tel" placeholder="Phone *" autoComplete="tel" data-testid="callback-phone-input" value={form.phone} onChange={set("phone")} className={inputCls} />
        <input required type="email" placeholder="Email *" autoComplete="email" data-testid="callback-email-input" value={form.email} onChange={set("email")} className={inputCls} />
        <select data-testid="callback-time-select" value={form.preferred_time} onChange={set("preferred_time")} className={`${inputCls} sm:col-span-2`}>
          {TIME_OPTIONS.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <textarea rows={3} placeholder="Anything Tory should know? (optional)" data-testid="callback-message-input" value={form.message} onChange={set("message")} className={`${inputCls} sm:col-span-2`} />
        <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" data-testid="callback-honeypot-input" value={form.company} onChange={set("company")} className="hidden" />
      </div>

      <button
        type="submit"
        disabled={sending}
        data-testid="callback-submit-button"
        className="group mt-6 inline-flex w-full items-center justify-center gap-3 rounded-full bg-ink px-8 py-4 text-sm font-semibold text-ivory transition-all duration-300 hover:-translate-y-0.5 hover:bg-eucalyptus hover:text-ivory disabled:opacity-60 disabled:hover:translate-y-0"
      >
        {sending ? "Sending…" : "See how my loan stacks up"}
        <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
      </button>
    </form>
  );
};

const Contact = () => (
  <section id="contact" className="relative overflow-hidden bg-cream py-24 text-ink lg:py-32">
    <img
      src={BG_IMG}
      alt=""
      aria-hidden="true"
      loading="lazy"
      className="pointer-events-none absolute right-0 top-0 h-full w-1/2 object-cover opacity-[0.10] saturate-[0.8] [mask-image:linear-gradient(to_left,black,transparent)]"
    />

    <div className="relative mx-auto max-w-[88rem] px-6 lg:px-12">
      <Reveal>
        <Eyebrow>Say hello</Eyebrow>
        <h2 className="mt-4 max-w-3xl font-display text-4xl font-medium leading-[1.08] tracking-tight sm:text-5xl lg:text-7xl">
          Is your home loan
          <br />
          <span className="italic text-eucalyptus">still competitive?</span>
        </h2>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
        <div className="lg:col-span-7">
          <Reveal delay={0.12}>
            <div className="border-t border-ink/10">
              <a
                href="tel:0414345736"
                data-testid="contact-call-link"
                className="group flex items-center justify-between gap-6 border-b border-ink/10 py-8 transition-colors duration-500 hover:bg-ink/[0.03] lg:py-10"
              >
                <div className="flex flex-col gap-2 sm:flex-row sm:items-baseline sm:gap-10">
                  <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-eucalyptus">Call</span>
                  <span className="font-display text-3xl font-medium transition-colors duration-300 group-hover:text-eucalyptus sm:text-5xl">
                    0414 345 736
                  </span>
                </div>
                <ArrowUpRight className="h-8 w-8 shrink-0 text-ink/30 transition-all duration-500 group-hover:translate-x-2 group-hover:-translate-y-2 group-hover:text-eucalyptus lg:h-10 lg:w-10" />
              </a>
              <a
                href="mailto:tory@torofinance.com.au"
                data-testid="contact-email-link"
                className="group flex items-center justify-between gap-6 border-b border-ink/10 py-8 transition-colors duration-500 hover:bg-ink/[0.03] lg:py-10"
              >
                <div className="flex flex-col gap-2 sm:flex-row sm:items-baseline sm:gap-10">
                  <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-eucalyptus">Email</span>
                  <span className="break-all font-display text-3xl font-medium transition-colors duration-300 group-hover:text-eucalyptus sm:text-5xl">
                    tory@torofinance.com.au
                  </span>
                </div>
                <ArrowUpRight className="h-8 w-8 shrink-0 text-ink/30 transition-all duration-500 group-hover:translate-x-2 group-hover:-translate-y-2 group-hover:text-eucalyptus lg:h-10 lg:w-10" />
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="mt-10 flex flex-col gap-8 sm:flex-row sm:items-center sm:justify-between" data-testid="contact-details">
              <p className="flex items-start gap-3 text-sm leading-relaxed text-ink/60">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-eucalyptus" />
                PO Box 4012, Lugarno NSW 2210
              </p>
              <div className="flex items-center gap-4">
                <a
                  href="https://www.facebook.com/ToroFinanceGroup/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-facebook-link"
                  aria-label="Toro Finance Group on Facebook"
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-ink/15 text-ink/60 transition-all duration-300 hover:-translate-y-1 hover:border-eucalyptus hover:text-eucalyptus"
                >
                  <Facebook className="h-4 w-4" />
                </a>
                <a
                  href="https://www.linkedin.com/in/tory-pizzolato/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-linkedin-link"
                  aria-label="Tory Pizzolato on LinkedIn"
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-ink/15 text-ink/60 transition-all duration-300 hover:-translate-y-1 hover:border-eucalyptus hover:text-eucalyptus"
                >
                  <Linkedin className="h-4 w-4" />
                </a>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.18} className="lg:col-span-5">
          <CallbackForm />
        </Reveal>
      </div>
    </div>
  </section>
);

export default Contact;
